import React from 'react';
import { useMutation, UseMutationOptions, UseMutationResult } from 'react-query';
import { ApiClient, ApiError, isServiceUnavailable } from '../async/api-client';
import { useModalManager } from '../contexts/modal-manager-context/ModalManagerProvider';
import { ApiErrorModalContent } from './api-error-modal-content/ApiErrorModalContent';
import { useApiClient } from '../async/UseApiClient';
import { reportApiError } from '../error-reporting/ErrorReporter';

export type ApiMutationFunction<TRequest, TResult = void> = (apiClient: ApiClient, request: TRequest) => Promise<TResult>;
export type UseApiMutationOptions<TRequest, TResult = void> = Omit<UseMutationOptions<TResult, ApiError, TRequest>, 'mutationFn'>;
export type UseApiMutationResult<TRequest, TResult = void> = UseMutationResult<TResult, ApiError, TRequest>;

export function useApiMutation<TRequest, TResult = void>(
  mutationFn: ApiMutationFunction<TRequest, TResult>,
  options?: UseApiMutationOptions<TRequest, TResult>,
): UseApiMutationResult<TRequest, TResult> {
  const modalManager = useModalManager();
  const apiClient = useApiClient();

  return useMutation<TResult, ApiError, TRequest>(
    (variables) => mutationFn(apiClient, variables),
    {
      retry: 0,
      ...options,
      onError: (error, variables, context) => {
        // Log every mutation error to VeloX — see UseApiQuery.
        try {
          reportApiError(error);
        } catch (reportingError) {
          console.error('Failed to report API error:', reportingError);
        }

        if (options?.onError) {
          // Isolate the caller's handler so a page-level bug can't block the error modal.
          try {
            options.onError(error, variables, context);
          } catch (callerError) {
            console.error('useApiMutation onError handler failed:', callerError);
          }
        }

        // An unavailable service always surfaces the modal — see UseApiQuery for rationale.
        if (!(options as any)?.skipGlobalError || isServiceUnavailable(error)) {
          modalManager.open({
            title: 'Error',
            content: () => React.createElement(ApiErrorModalContent, { error }),
            actions: [
              {
                title: 'Close',
                type: 'primary',
                key: 'close',
                onClick(close) {
                  close();
                },
              },
            ],
          });
        }
      },
    }
  );
}
